import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface CoachNotif {
  id: string;
  title: string;
  body: string;
  candidateUid?: string;
  sentAt: any;
  read: boolean;
}

function fmtAge(ts: any): string {
  const ms = ts?.toMillis ? ts.toMillis() : ts?.seconds ? ts.seconds * 1000 : 0;
  if (!ms) return '';
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function CoachBell({ coachId }: { coachId: string }) {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState<CoachNotif[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'coachNotifications', coachId, 'entries'),
      orderBy('sentAt', 'desc'),
    );
    return onSnapshot(q, snap => {
      setNotifs(snap.docs.map(d => ({ id: d.id, ...d.data() } as CoachNotif)));
    });
  }, [coachId]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unread = notifs.filter(n => !n.read).length;

  const handleClick = async (n: CoachNotif) => {
    setOpen(false);
    if (!n.read) {
      updateDoc(doc(db, 'coachNotifications', coachId, 'entries', n.id), { read: true }).catch(() => {});
    }
    if (n.candidateUid) navigate(`/coach/candidate/${n.candidateUid}`);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Notifications"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 6px',
          position: 'relative',
          color: 'var(--text-secondary)',
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: 'var(--error)',
            color: '#fff',
            borderRadius: '999px',
            fontSize: 10,
            fontWeight: 700,
            minWidth: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 3px',
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          right: 0,
          top: 'calc(100% + 8px)',
          width: 320,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 200,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Notifications
          </div>
          {notifs.length === 0 ? (
            <div style={{ padding: '20px 14px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
              No notifications
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {notifs.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--border-light)',
                    cursor: n.candidateUid ? 'pointer' : 'default',
                    background: n.read ? 'transparent' : 'var(--accent-dim)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: n.read ? 400 : 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                      {n.title}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {fmtAge(n.sentAt)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    {n.body}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const LAST_PORTAL_KEY = 'klarr_last_portal';

function RoleSwitcher() {
  const { firmAdmin, coach } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (!firmAdmin || !coach) return null;

  // Default to firm side when not explicitly on /coach/*
  const isFirmActive = !location.pathname.startsWith('/coach');

  const switchTo = (portal: 'firm' | 'coach') => {
    localStorage.setItem(LAST_PORTAL_KEY, portal);
    navigate(portal === 'firm' ? '/firm/dashboard' : '/coach/caseload', { replace: true });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        marginLeft: 16,
        background: 'var(--bg-secondary)',
        borderRadius: 6,
        padding: 2,
      }}
    >
      <button
        className={`filter-btn${isFirmActive ? ' active' : ''}`}
        onClick={() => switchTo('firm')}
        style={{ fontSize: 12, padding: '3px 10px' }}
      >
        Firm
      </button>
      <button
        className={`filter-btn${!isFirmActive ? ' active' : ''}`}
        onClick={() => switchTo('coach')}
        style={{ fontSize: 12, padding: '3px 10px' }}
      >
        Coach
      </button>
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, collegeAdmin, firmAdmin, coach, signOut } = useAuth();
  const location = useLocation();

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `nav-link${isActive ? ' active' : ''}`;

  const isDualRole = !!(firmAdmin && coach);
  // For dual-role: show firm nav unless explicitly on /coach/*, show coach nav on /coach/*
  const showFirmNav = firmAdmin && (!isDualRole || !location.pathname.startsWith('/coach'));
  const showCoachNav = coach && (!isDualRole || location.pathname.startsWith('/coach'));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <nav className="nav">
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <div className="nav-brand">
            <span>Klarr</span>
          </div>
          {user && (
            <div className="nav-links">
              <NavLink to="/dashboard" className={navLinkClass}>
                Colleges
              </NavLink>
              <NavLink to="/firms" className={navLinkClass}>
                Outplacement
              </NavLink>
              <NavLink to="/users" className={navLinkClass}>
                Users
              </NavLink>
              <NavLink to="/outreach" className={navLinkClass}>
                Outreach
              </NavLink>
              <NavLink to="/coach-applications" className={navLinkClass}>
                Independent Coaches
              </NavLink>
            </div>
          )}
          {showFirmNav && (
            <>
              <div style={{ marginLeft: 24, fontSize: 13, color: 'var(--text-secondary)' }}>
                {firmAdmin!.firm.name}
              </div>
              <div className="nav-links" style={{ marginLeft: 24 }}>
                <NavLink to="/firm/dashboard" className={navLinkClass}>
                  Dashboard
                </NavLink>
                <NavLink to="/firm/roster" className={navLinkClass}>
                  Roster
                </NavLink>
                <NavLink to="/firm/coaches" className={navLinkClass}>
                  Coaches
                </NavLink>
                <NavLink to="/firm/seats" className={navLinkClass}>
                  Seats
                </NavLink>
                <NavLink to="/firm/clients" className={navLinkClass}>
                  Clients
                </NavLink>
              </div>
            </>
          )}
          {showCoachNav && (
            <>
              <div style={{ marginLeft: 24, fontSize: 13, color: 'var(--text-secondary)' }}>
                {coach!.coach.name}
              </div>
              <div className="nav-links" style={{ marginLeft: 24 }}>
                <NavLink to="/coach/caseload" className={navLinkClass}>
                  Caseload
                </NavLink>
              </div>
            </>
          )}
          {isDualRole && <RoleSwitcher />}
          {collegeAdmin && (
            <div style={{ marginLeft: 24, fontSize: 13, color: 'var(--text-secondary)' }}>
              {collegeAdmin.account.name}
            </div>
          )}
        </div>
        <div className="nav-user">
          {coach && <CoachBell coachId={coach.coachId} />}
          <span className="nav-email">
            {user?.email ?? firmAdmin?.firm.contactEmail ?? coach?.coach.email ?? collegeAdmin?.account.contactEmail}
          </span>
          <button
            className="btn-secondary"
            onClick={signOut}
            style={{ padding: '6px 14px', fontSize: 13 }}
          >
            Sign out
          </button>
        </div>
      </nav>
      <main className="main-content">{children}</main>
    </div>
  );
}
